/*
 * <bundle-group-controls> — one qty stepper + one remove control for a whole
 * bundle group in the native cart drawer. Applies the same quantity to every
 * line in the group via /cart/update.js, then re-renders the drawer through
 * CartDrawer.renderContents (same section-render flow Dawn's own cart uses).
 *
 * data-keys: comma-separated cart line keys of the group
 * data-qty:  current per-line quantity of the group
 */
(function () {
  'use strict';

  class BundleGroupControls extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
    }

    get keys() {
      return (this.dataset.keys || '').split(',').filter(Boolean);
    }

    get quantity() {
      return Number(this.dataset.qty) || 1;
    }

    onClick(event) {
      var control = event.target.closest('[data-bg-action]');
      if (!control) return;
      event.preventDefault();
      var action = control.getAttribute('data-bg-action');
      if (action === 'remove') {
        this.setGroupQuantity(0);
      } else if (action === 'inc') {
        this.setGroupQuantity(this.quantity + 1);
      } else if (action === 'dec') {
        this.setGroupQuantity(Math.max(0, this.quantity - 1));
      }
    }

    setGroupQuantity(quantity) {
      if (this.classList.contains('ccb-busy')) return;
      this.classList.add('ccb-busy');

      // On the /cart page (not inside the drawer) there is no section-render
      // target — update the cart then reload for a full server re-render.
      var inDrawer = !!this.closest('cart-drawer');
      var drawer = inDrawer ? document.querySelector('cart-drawer') : null;
      var updates = {};
      this.keys.forEach(function (key) {
        updates[key] = quantity;
      });
      var sections = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map(function (section) { return section.id; })
        : ['cart-drawer', 'cart-icon-bubble'];

      fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          updates: updates,
          sections: sections,
          sections_url: window.location.pathname
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart update failed (' + res.status + ')');
          return res.json();
        })
        .then(function (state) {
          if (!inDrawer) {
            window.location.reload();
            return;
          }
          if (drawer && state.sections) {
            drawer.renderContents(state);
            var isEmpty = state.item_count === 0;
            drawer.classList.toggle('is-empty', isEmpty);
            var itemsEl = drawer.querySelector('cart-drawer-items');
            if (itemsEl) itemsEl.classList.toggle('is-empty', isEmpty);
            var inner = drawer.querySelector('.drawer__inner');
            if (inner) inner.classList.toggle('is-empty', isEmpty);
          } else {
            window.location.reload();
          }
        })
        .catch(function () {
          window.location.reload();
        });
      // No .finally() reset: renderContents replaces this element entirely.
    }
  }

  if (!customElements.get('bundle-group-controls')) {
    customElements.define('bundle-group-controls', BundleGroupControls);
  }

  // One-time bundle → subscription upsell: applies each component's selling
  // plan via sequential /cart/change.js calls, then re-renders the drawer once.
  // The plan map is keyed by VARIANT id, not line key: a plan change re-keys
  // every line in the cart, so each call resolves its line's fresh key from
  // the previous response's cart state. The bundle discount Function then
  // applies on its own because the lines are subscribed.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-ccb-bundle-upsell]');
    if (!btn || btn.classList.contains('ccb-busy')) return;
    event.preventDefault();
    btn.classList.add('ccb-busy');

    var planMap = null;
    try {
      planMap = JSON.parse(btn.getAttribute('data-plan-map'));
    } catch (err) {
      planMap = null;
    }
    var variantIds = planMap ? Object.keys(planMap) : [];
    var instance = btn.getAttribute('data-instance');
    if (!variantIds.length || !instance) {
      btn.classList.remove('ccb-busy');
      return;
    }

    var drawer = document.querySelector('cart-drawer');
    var sections = drawer && typeof drawer.getSectionsToRender === 'function'
      ? drawer.getSectionsToRender().map(function (section) { return section.id; })
      : ['cart-drawer', 'cart-icon-bubble'];
    var changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
    var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';

    var findLine = function (cartState, variantId) {
      return (cartState.items || []).filter(function (line) {
        return String(line.variant_id) === variantId &&
          line.properties &&
          line.properties._bundle_instance === instance &&
          !line.selling_plan_allocation;
      })[0];
    };

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart read failed (' + res.status + ')');
        return res.json();
      })
      .then(function (initialState) {
        return variantIds.reduce(function (chain, variantId) {
          return chain.then(function (cartState) {
            var line = findLine(cartState, variantId);
            if (!line) return cartState;
            return fetch(changeUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({
                id: line.key,
                quantity: line.quantity,
                selling_plan: planMap[variantId]
              })
            }).then(function (res) {
              if (!res.ok) throw new Error('Bundle plan switch failed (' + res.status + ')');
              return res.json();
            });
          });
        }, Promise.resolve(initialState));
      })
      .then(function () {
        // Dedicated section-render request so the drawer refresh never depends
        // on which change call ran last.
        return fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            updates: {},
            sections: sections,
            sections_url: window.location.pathname
          })
        }).then(function (res) {
          if (!res.ok) throw new Error('Cart render failed (' + res.status + ')');
          return res.json();
        });
      })
      .then(function (state) {
        if (btn.closest('cart-drawer') && drawer && state && state.sections) {
          drawer.renderContents(state);
        } else {
          window.location.reload();
        }
      })
      .catch(function () {
        window.location.reload();
      });
  }, true);

  // One-time → subscription upsell on loose cart lines: switches the line to
  // its selling plan via /cart/change.js, then re-renders the drawer.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-ccb-upsell]');
    if (!btn || btn.classList.contains('ccb-busy')) return;
    event.preventDefault();
    btn.classList.add('ccb-busy');

    var drawer = document.querySelector('cart-drawer');
    var sections = drawer && typeof drawer.getSectionsToRender === 'function'
      ? drawer.getSectionsToRender().map(function (section) { return section.id; })
      : ['cart-drawer', 'cart-icon-bubble'];

    fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: btn.getAttribute('data-line-key'),
        quantity: Number(btn.getAttribute('data-line-qty')) || 1,
        selling_plan: Number(btn.getAttribute('data-selling-plan')),
        sections: sections,
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Plan switch failed (' + res.status + ')');
        return res.json();
      })
      .then(function (state) {
        if (btn.closest('cart-drawer') && drawer && state.sections) {
          drawer.renderContents(state);
        } else {
          window.location.reload();
        }
      })
      .catch(function () {
        window.location.reload();
      });
  }, true);
})();
